import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Avatar } from './Avatar';
import type { Challenge, MadeThing, Work } from '../data/types';
import { guideFor, type CrewId } from '../data/crew';
import { photoForCard } from '../lib/photo';
import { asUrl, hostOf, linkLabel, madeId, madeSummary, rollIdeas, type Idea } from '../lib/made';
import { loadProfile, saveProfile } from '../lib/state';
import { getChallenge } from '../data/exercises';
import { writeUserDoc } from '../lib/feed';
import { DREAMS, dreamLabels, dreamPhrase } from '../data/intro-copy';
import { track } from '../analytics';
import { inAppBrowser } from '../lib/in-app';

/**
 * "Who are you?" (Day 2), one question at a time, the way Finch asks: somebody
 * you know asks it in a bubble, and the answer is written straight onto the
 * thing it becomes — a name tag, a pinboard, an index card — never into a form.
 *
 *  1. Hello        a HELLO sticker that is your face and your name, and
 *                  nothing else to answer (P6, 2026-10-02: uploading your
 *                  image and your name is the most important thing). What you
 *                  said you would like to be is shown on it, not asked.
 *  2. Show your    a pinboard of your real work: photos and screenshots off
 *     work         your phone, links (with a screenshot if you like), or just
 *                  the words for it. Anything made counts, and says so; and
 *                  "nothing yet" is answered, not skipped: Day 1 goes up first.
 *  3. Next         what you would like to be (the first day's picks, a tap
 *                  changes them), then the thing you want to make, with dice
 *                  that deal three ideas from that work, aimed at the big goal
 *
 * Writes `work.name`, `fields.what` (the first day's picks), `fields.made` (the
 * words for the pinboard) and `fields.next`; the things themselves are
 * `work.made`. The name stays out of `fields`, which the crew read.
 *
 * Cast on Day 1 (well/product/32 §5): the name and the photo were given then,
 * and the crew have drawn you, so it opens on "Show your work" and the reward
 * is that "me", redrawn among your work (`work.drawn`).
 */

/** Small enough that six of them fit on one card under the backend's ceiling. */
const MADE_EDGE = 900;
const MAX_THINGS = 12;
/** What "Show your work" asks for: at least this many, and room on the board for this many. */
const HOPE_FOR = 3;
const BOARD = 5;
type Beat = 0 | 1 | 2;
type Adding = null | 'link' | 'note';
/** A link or a note typed into its sheet but not pinned yet: Next pins it rather than dropping it. */
type Draft = { flush: () => void; busy: boolean };
/** The pin "nothing yet" puts up: Day 1, which they have made. */
const START_ID = 'day1';

export function WhoAmI({ challenge, work, onChange, onProgress, onFinish, onPhotoBusy, guide }: {
  challenge: Challenge; work: Work; onChange: (w: Work) => void;
  onProgress?: (p: { filled: number; count: number } | null) => void;
  onFinish?: (w: Work) => void; onPhotoBusy?: (busy: boolean) => void; guide?: CrewId;
}) {
  const asker = guide ?? guideFor(challenge.id).id;
  // drawn already: the sticker is done, so it starts at the pinboard
  const [cast] = useState(() => { const p = loadProfile(); return !!(p.castMe && p.name); });
  // drawn already but not named (Put Yourself In It, screens/PutMeIn.tsx): the sticker shows that drawing and asks only the name
  const [me] = useState(() => loadProfile().castMe);
  const first: Beat = cast ? 1 : 0;
  const [beat, setBeat] = useState<Beat>(first);
  const latest = useRef(work);
  useLayoutEffect(() => { latest.current = work; });
  const F = work.fields || {};
  const made = work.made || [];
  const [picks, setPicks] = useState<string[]>(() => loadProfile().dreams || []);
  const [selfieBusy, setSelfieBusy] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [developing, setDeveloping] = useState(false);
  const photoBusy = useCallback((b: boolean) => { setDeveloping(b); onPhotoBusy?.(b); }, [onPhotoBusy]);
  const goals = dreamLabels(picks);
  /** A tap adds or takes away a goal, on the badge and on the profile the dice and the crew aim at. */
  const togglePick = (id: string) => {
    const next = picks.includes(id) ? picks.filter((x) => x !== id) : [...picks.filter((x) => x !== 'unsure'), id];
    setPicks(next);
    saveProfile({ ...loadProfile(), dreams: next, dream: next[0] });
    setField('what', dreamPhrase(next));
    track('whoami_goal', { id, on: next.includes(id) });
  };

  useEffect(() => {
    if (!cast) return;
    const w = latest.current;
    if (!w.drawn || !w.name) commit({ ...w, name: w.name || loadProfile().name, drawn: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cast]);
  useEffect(() => { onProgress?.({ filled: beat + 1 - first, count: 3 - first }); }, [beat, first, onProgress]);
  useEffect(() => () => onProgress?.(null), [onProgress]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [beat]);

  // Through the ref, not the render: three photographs can land in one tick,
  // and each must see the one before it.
  const commit = (w: Work) => { latest.current = w; onChange(w); };
  const setField = (k: string, v: string) => commit({ ...latest.current, fields: { ...(latest.current.fields || {}), [k]: v } });
  const setMade = (next: MadeThing[]) => {
    const w = latest.current;
    commit({ ...w, made: next, fields: { ...(w.fields || {}), made: madeSummary(next) } });
  };

  const go = (to: Beat) => { track('whoami_step', { to, things: (latest.current.made || []).length }); setBeat(to); };
  /** Leaving the board with a sheet half filled in means "and that one too", not "never mind". */
  const leaveBoard = (to: Beat) => { draft?.flush(); go(to); };
  /** "Nothing yet": they have, though. Day 1 is pinned up as the first thing on the board. */
  const startHere = () => {
    const d1 = getChallenge(loadProfile().days?.[1]?.challengeId || '');
    setMade([{ id: START_ID, kind: 'note', caption: d1 ? `Day 1: ${d1.title}`.slice(0, 60) : 'Day 1, here' }, ...(latest.current.made || [])]);
    track('whoami_nothing_yet', { day1: !!d1 });
  };
  const started = made.some((m) => m.id === START_ID);
  /** The name is theirs everywhere, not only on the badge: a friend's link says it too. */
  const keepName = () => {
    const name = (latest.current.name || '').trim().slice(0, 40);
    if (name && name !== loadProfile().name) { saveProfile({ ...loadProfile(), name }); writeUserDoc({ name }).catch(() => {}); }
    go(1);
  };

  return (
    <div className="who">
      <div className="who-steps" aria-hidden="true">{([0, 1, 2] as Beat[]).filter((i) => i >= first).map((i) => <i key={i} className={i <= beat ? 'on' : ''} />)}</div>
      {beat === 0 && (
        <section className="who-beat" key="what">
          <Ask who={asker}>Who are you?<small>{me ? 'The crew have your face. Now your name.' : 'Start with your face and your name.'}</small></Ask>
          <NameTag value={work.name || ''} onChange={(v) => commit({ ...latest.current, name: v })} goals={goals} me={me}
            selfie={work.selfie} onSelfie={(selfie) => commit({ ...latest.current, selfie, ...(selfie ? {} : { showSelfie: false }) })} onBusy={setSelfieBusy} />
          {/* the photo is for their character first; on the badge only if they say so, since the badge is what gets shared */}
          {work.selfie && (
            <label className={`selfie-opt ${work.showSelfie ? 'on' : ''}`}>
              <input type="checkbox" checked={!!work.showSelfie} onChange={(e) => { commit({ ...latest.current, showSelfie: e.target.checked }); track('whoami_selfie_shown', { on: e.target.checked }); }} />
              <span className="clock-box" aria-hidden="true">{work.showSelfie ? '✓' : ''}</span>
              <span>Show my photo on my badge<small>Either way, your crew use it to draw you.</small></span>
            </label>
          )}
          <div className="who-foot">
            <button className="btn coral" disabled={!(work.name || '').trim() || selfieBusy} onClick={keepName}>{selfieBusy ? 'Adding your photo…' : 'Next'}</button>
          </div>
        </section>
      )}
      {beat === 1 && (
        <section className="who-beat" key="made">
          {started && made.length === 1
            ? <Ask who={asker}>Then this is where it starts.<small>Day 1 goes up first. Pin the rest up as you make it.</small></Ask>
            : <Ask who={asker}>Show your work.<small>Anything you have made counts: a ring dish, a dinner, a paint-by-numbers, a page, something you built in Claude.</small></Ask>}
          <Pinboard made={made} setMade={setMade} latest={latest} onPhotoBusy={photoBusy} challengeId={challenge.id} onDraft={setDraft} />
          <div className="who-foot">
            {/* nothing to pin is an answer too: Day 1 goes up, and the board starts there */}
            {made.length || draft || developing
              ? <button className="btn coral" disabled={!!draft?.busy} onClick={() => leaveBoard(2)}>{draft?.busy ? 'Adding it…' : 'Next'}</button>
              : <button className="btn butter" onClick={startHere}>I haven’t made anything yet</button>}
            {!cast && <button className="link" disabled={!!draft?.busy} onClick={() => leaveBoard(0)}>Back</button>}
          </div>
        </section>
      )}
      {beat === 2 && (
        <section className="who-beat" key="next">
          <Ask who={asker}>What do you want to make next?</Ask>
          {/* what they would like to be: the first day's picks lit up, and a tap changes them; the dice aim at it */}
          <div className="who-goals">
            <span className="label">{picks.length ? 'You said you would like to be' : 'What would you like to be?'}</span>
            <div className="chips who-chips">
              {DREAMS.filter((d) => d.id !== 'unsure').map((d) => (
                <button type="button" key={d.id} className={`chip ${picks.includes(d.id) ? 'on' : ''}`} aria-pressed={picks.includes(d.id)} onClick={() => togglePick(d.id)}>{d.label}</button>
              ))}
            </div>
          </div>
          <NextCard value={F.next || ''} onChange={(v) => setField('next', v)} made={made} goals={goals} />
          <div className="who-foot">
            <button className="btn" onClick={() => { track('whoami_done', { things: made.length, photos: made.filter((m) => m.kind === 'photo').length, links: made.filter((m) => m.kind === 'link').length, next: !!(F.next || '').trim() }); onFinish?.(latest.current); }}>
              {loadProfile().crew === 'off' ? 'Done' : 'Draw me'}
            </button>
            <button className="link" onClick={() => go(1)}>Back</button>
          </div>
        </section>
      )}
    </div>
  );
}

/** The question, in a bubble from whoever is here today. */
function Ask({ who, children }: { who: CrewId; children: React.ReactNode }) {
  return (
    <div className="who-ask">
      <Avatar who={who} size={56} />
      <p className="who-bubble">{children}</p>
    </div>
  );
}

// ── 1. the name tag ───────────────────────────────────────────────────────────

/** "A writer" → "Writer": the picks as they read on a sticker. */
const bareLabel = (s: string) => { const t = s.replace(/^(a|an)\s+/i, ''); return t.charAt(0).toUpperCase() + t.slice(1); };

/** Also the HELLO sticker of "Want to be in it?" on Day 1 (components/Cast.tsx), with the camera first. */
export function NameTag({ value, onChange, goals, selfie, onSelfie, onBusy, camera, source = 'what-i-am', me }: { value: string; onChange: (v: string) => void; goals: string[]; selfie?: string; onSelfie: (s: string | undefined, from?: 'camera' | 'library') => void; onBusy: (b: boolean) => void; camera?: boolean; source?: string; me?: string }) {
  const ref = useGrow(value);
  return (
    <div className="nametag">
      <div className="nametag-top"><b>HELLO</b><span>my name is</span></div>
      <div className="nametag-body">
        {/* the crew drew them already: that drawing is their face here, and the next drawing of them reads it (meLikeness) */}
        {me && !selfie
          ? <div className="selfie"><span className="selfie-spot has"><img src={me} alt="You, as the crew drew you" /></span></div>
          : <SelfieSpot selfie={selfie} onSelfie={onSelfie} onBusy={onBusy} camera={camera} source={source} />}
        <textarea ref={ref} rows={1} value={value} onChange={(e) => onChange(e.target.value.replace(/\n/g, ' '))} placeholder="Your name" aria-label="Your name" maxLength={40} autoComplete="given-name" enterKeyHint="done" />
        {/* what they would like to be, the way the badge will say it */}
        {goals.length > 0 && <div className="nametag-goals">{goals.map(bareLabel).join(' · ')}</div>}
      </div>
    </div>
  );
}

/** How long the picker may stay silent, once the page is back in front, before it counts as not working. */
const SILENT_MS = 15_000;

/**
 * A photo of you, in the middle of the sticker: the first thing the beat asks
 * for (P6), but still optional and never counted toward anything (fear of
 * judgement was interview 5's theme). It is what
 * makes the character the crew draw look like you (backend
 * kindling-render.service LIKENESS). No `capture`: a phone offers the camera
 * and the library both, and plenty of people have a photo they like better
 * than the one they would take now. With `camera` (Day 1's cast, 32 §2 step 3)
 * the spot opens the front lens and the library is the line under it. The
 * photo step of Imagine Yourself There (screens/Imagine.tsx) uses it the same way.
 */
export function SelfieSpot({ selfie, onSelfie, onBusy, camera, source = 'what-i-am', label = 'Your photo', onSilent }: { selfie?: string; onSelfie: (s: string | undefined, from?: 'camera' | 'library') => void; onBusy: (b: boolean) => void; camera?: boolean; source?: string; label?: string; onSilent?: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The picker, counted from both ends: opened, and then a photo, a cancel, or
   * nothing at all. Inside Facebook's and Instagram's browsers "opened, and
   * nothing came back" is how a picker that does not work there would look
   * (or a camera that reloaded the page), as distinct from somebody who never
   * tried. `in_app` says which browser, so the two can be read apart per app.
   */
  const inApp = inAppBrowser() ?? 'none';
  /**
   * "Opened, and nothing came back" as a number of its own (2026-10-30: two
   * people on Facebook's Android browser tapped twelve times and got nothing,
   * not even a cancel): SILENT_MS after the page is back in front with the
   * picker still owing an answer, `photo_picker_silent`, and `onSilent` so the
   * screen can offer the way out.
   */
  const owed = useRef<{ from: 'camera' | 'library'; back: number } | null>(null);
  const settle = () => { owed.current = null; };
  useEffect(() => {
    const onShow = () => { if (owed.current && document.visibilityState === 'visible') owed.current.back = Date.now(); };
    document.addEventListener('visibilitychange', onShow);
    const t = setInterval(() => {
      const o = owed.current;
      if (!o || document.visibilityState !== 'visible' || Date.now() - o.back < SILENT_MS) return;
      owed.current = null;
      track('photo_picker_silent', { from: o.from, source, in_app: inApp });
      onSilent?.();
    }, 1000);
    return () => { document.removeEventListener('visibilitychange', onShow); clearInterval(t); };
  }, [source, inApp, onSilent]);
  const open = (from: 'camera' | 'library') => {
    track('photo_picker_opened', { from, source, in_app: inApp });
    owed.current = { from, back: Date.now() };
    (from === 'camera' ? camRef : fileRef).current?.click();
  };
  useEffect(() => {
    const listen = (el: HTMLInputElement | null, from: 'camera' | 'library') => {
      if (!el) return () => {};
      // `cancel` on a file input: Safari 16.4+, Chrome 113+; older browsers simply never send it
      const onCancel = () => { settle(); track('photo_picker_cancelled', { from, source, in_app: inApp }); };
      el.addEventListener('cancel', onCancel);
      return () => el.removeEventListener('cancel', onCancel);
    };
    const a = listen(fileRef.current, 'library'), b = listen(camRef.current, 'camera');
    return () => { a(); b(); };
  }, [camera, source, inApp]);
  const onFile = (f: File | undefined, from: 'camera' | 'library') => {
    settle();
    if (!f) { track('photo_picker_empty', { from, source, in_app: inApp }); return; }
    setBusy(true); onBusy(true);
    void photoForCard(f, MADE_EDGE)
      .then((p) => { onSelfie(p, from); track('whoami_selfie', { added: true, from, source, in_app: inApp }); })
      .catch((e) => track('photo_failed', { challengeId: source, error: String(e).slice(0, 120), in_app: inApp }))
      .finally(() => { setBusy(false); onBusy(false); if (fileRef.current) fileRef.current.value = ''; if (camRef.current) camRef.current.value = ''; });
  };
  return (
    <div className="selfie">
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0], 'library')} />
      {camera && <input ref={camRef} type="file" accept="image/*" capture="user" hidden onChange={(e) => onFile(e.target.files?.[0], 'camera')} />}
      <button type="button" className={`selfie-spot ${selfie ? 'has' : ''} ${busy ? 'busy' : ''}`} onClick={() => open(camera ? 'camera' : 'library')} aria-label={selfie ? 'Change your photo' : 'Add a photo of you'}>
        {selfie ? <img src={selfie} alt="You" /> : <><span className="selfie-plus" aria-hidden="true">📷</span><span className="selfie-word">{busy ? 'Adding…' : label}</span></>}
      </button>
      {selfie
        ? <button type="button" className="unpin selfie-x" aria-label="Take your photo off" onClick={() => { onSelfie(undefined); track('whoami_selfie', { added: false, source }); }}>×</button>
        : camera
          ? <button type="button" className="link selfie-library" onClick={() => open('library')}>or choose one from your photos</button>
          : <span className="selfie-hint">Tap to add a photo of you · optional</span>}
    </div>
  );
}

/** A textarea that grows with its words instead of scrolling inside itself. */
function useGrow(value: string) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return ref;
}

// ── 2. the pinboard ───────────────────────────────────────────────────────────

const TILT = [-2.4, 1.8, -1.2, 2.6, -2, 1.1];

/**
 * The empty spaces on the board, each saying what could go there. They fill
 * the board up to five, so three to five reads as the shape of the thing and
 * not a rule. The Claude one always leads until something made in Claude is
 * pinned; the rest are dealt from this pool, and the die on the board deals
 * again (owner, 2026-09-29).
 */
type Ghost = { kind: 'photo' | 'link' | 'note'; label: string; claude?: boolean };
const CLAUDE_GHOST: Ghost = { kind: 'link', label: 'Something cool you made in Claude', claude: true };
const GHOST_POOL: Ghost[] = [
  { kind: 'photo', label: 'A dinner you cooked' },
  { kind: 'photo', label: 'A ring dish, a mug, a pot' },
  { kind: 'photo', label: 'A paint-by-numbers' },
  { kind: 'photo', label: 'A photo of anything you are proud of' },
  { kind: 'photo', label: 'A drawing or a notebook page' },
  { kind: 'photo', label: 'A screenshot of something you built' },
  { kind: 'link', label: 'Your site, a song, a video' },
  { kind: 'photo', label: 'Something you sewed or fixed' },
  { kind: 'photo', label: 'A page you wrote' },
  { kind: 'photo', label: 'A doodle from a meeting' },
  { kind: 'link', label: 'A post you are proud of' },
  { kind: 'photo', label: 'A thing you made for someone' },
  { kind: 'photo', label: 'Something from when you were a kid' },
  { kind: 'note', label: 'Anything else, in words' },
];
/**
 * The first deal is the everyday ones, so a dinner and a ring dish plainly
 * count (P6, 2026-10-02), then the photo-you-are-proud-of, which fits anybody.
 */
const FIRST_DEAL = [0, 1, 2, 3, 13];
const deal = (): number[] => [...GHOST_POOL.keys()].sort(() => Math.random() - 0.5);

function Pinboard({ made, setMade, latest, onPhotoBusy, challengeId, onDraft }: { made: MadeThing[]; setMade: (m: MadeThing[]) => void; latest: React.MutableRefObject<Work>; onPhotoBusy?: (b: boolean) => void; challengeId: string; onDraft: (d: Draft | null) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState<Adding>(null);
  const [developing, setDeveloping] = useState(0);
  const full = made.length + developing >= MAX_THINGS;
  const hasClaude = made.some((m) => m.kind === 'link' && m.url && linkLabel(m.url) === 'Made with Claude');
  const [order, setOrder] = useState<number[]>(FIRST_DEAL);
  const [shuffles, setShuffles] = useState(0);
  const ghosts = [...(hasClaude ? [] : [CLAUDE_GHOST]), ...order.map((i) => GHOST_POOL[i])].slice(0, Math.max(0, BOARD - made.length - developing));
  const shuffle = () => { setOrder(deal()); setShuffles((n) => n + 1); track('whoami_shuffled', { things: made.length }); };

  useEffect(() => { onPhotoBusy?.(developing > 0); }, [developing, onPhotoBusy]);

  /** Each photograph is shrunk as it lands; a slow one never holds up the others. */
  const onFiles = (files: FileList | null) => {
    const list = Array.from(files || []).slice(0, MAX_THINGS - made.length - developing);
    if (!list.length) return;
    setDeveloping((n) => n + list.length);
    track('whoami_photos', { n: list.length, challengeId });
    for (const f of list) {
      void photoForCard(f, MADE_EDGE)
        .then((photo) => { const now = latest.current.made || []; setMade([...now, { id: madeId(), kind: 'photo', photo }]); })
        .catch((e) => track('photo_failed', { challengeId, error: String(e).slice(0, 120) }))
        .finally(() => setDeveloping((n) => n - 1));
    }
    if (fileRef.current) fileRef.current.value = '';
  };
  const update = (id: string, patch: Partial<MadeThing>) => setMade((latest.current.made || []).map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const remove = (id: string) => setMade((latest.current.made || []).filter((m) => m.id !== id));
  const add = (m: Omit<MadeThing, 'id'>) => { setMade([...(latest.current.made || []), { ...m, id: madeId() }]); setAdding(null); track('whoami_added', { kind: m.kind, screenshot: !!m.photo }); };
  const open = (kind: 'photo' | 'link' | 'note') => { if (full) return; if (kind === 'photo') fileRef.current?.click(); else setAdding(kind); };
  const n = made.length;

  return (
    <div className="stack" style={{ gap: 14 }}>
      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} />
      <div className="pinboard">
        {/* stuck for what to pin: deal the suggestions again */}
        {!adding && ghosts.length > 0 && (
          <button type="button" className="board-dice" onClick={shuffle} aria-label="Other suggestions">
            <span className="die small" aria-hidden="true" key={shuffles}><i /><i /><i /><i /><i /></span>
          </button>
        )}
        {made.map((m, i) => <Pinned key={m.id} m={m} tilt={TILT[i % TILT.length]} onCaption={(c) => update(m.id, { caption: c })} onRemove={() => remove(m.id)} />)}
        {Array.from({ length: developing }, (_, i) => <div key={`dev${i}`} className="pinup polaroid developing" style={{ '--tilt': `${TILT[(made.length + i) % TILT.length]}deg` } as React.CSSProperties}><div className="polaroid-pic" /><span className="polaroid-cap">Developing…</span></div>)}
        {!adding && ghosts.map((g, i) => (
          <button type="button" key={`${shuffles}:${g.label}`} className={`pin-ghost ${g.claude ? 'claude' : ''}`} style={{ animationDelay: `${i * 60}ms` }} onClick={() => open(g.kind)}>
            <span className="ghost-plus">+</span>
            <span>{g.label}</span>
          </button>
        ))}
      </div>

      {adding === 'link' && <AddLink onAdd={(url, caption, photo) => add({ kind: 'link', url, caption, ...(photo ? { photo } : {}) })} onCancel={() => setAdding(null)} onDraft={onDraft} />}
      {adding === 'note' && <AddNote onAdd={(caption) => add({ kind: 'note', caption })} onCancel={() => setAdding(null)} onDraft={onDraft} />}

      {!adding && (
        <div className="add-row">
          <button type="button" className="add-btn" disabled={full} onClick={() => open('photo')}><span className="add-ico">📷</span><span>Photos &amp; screenshots</span></button>
          <button type="button" className="add-btn" disabled={full} onClick={() => open('link')}><span className="add-ico">🔗</span><span>A link</span></button>
          <button type="button" className="add-btn" disabled={full} onClick={() => open('note')}><span className="add-ico">✏️</span><span>Say it</span></button>
        </div>
      )}
      <p className={`board-count ${n >= HOPE_FOR ? 'met' : ''}`}>
        {n === 1 && made[0].id === START_ID ? 'Day 1 counts. The board fills as you go.'
          : n === 0 ? 'Start with one. Three is the goal.'
          : n < HOPE_FOR ? `${n} of ${HOPE_FOR}. ${HOPE_FOR - n === 1 ? 'One more?' : 'Keep going.'}`
            : n < BOARD ? `${n} things. Lovely. Room for ${BOARD - n} more.`
              : `${n} things. That is a body of work.`}
        {n > 0 && <span className="hint"> Tap a caption to say what it is.</span>}
      </p>
    </div>
  );
}

function Pinned({ m, tilt, onCaption, onRemove }: { m: MadeThing; tilt: number; onCaption: (c: string) => void; onRemove: () => void }) {
  const style = { '--tilt': `${tilt}deg` } as React.CSSProperties;
  const capRef = useGrow(m.caption || '');
  // a caption wraps rather than scrolling out of sight inside a narrow polaroid
  const cap = (placeholder: string, cls: string) => (
    <textarea ref={capRef} rows={1} className={cls} value={m.caption || ''} onChange={(e) => onCaption(e.target.value.replace(/\n/g, ' '))} placeholder={placeholder} maxLength={60} aria-label="What is it?" enterKeyHint="done"
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }} />
  );
  const x = <button type="button" className="unpin" aria-label="Take it off" onClick={onRemove}>×</button>;
  if (m.kind === 'link' && m.url) {
    return (
      <div className={`pinup ticket ${m.photo ? 'with-shot' : ''}`} style={style}>{x}
        {m.photo && <img className="ticket-shot" src={m.photo} alt="" />}
        <span className="ticket-kind">{linkLabel(m.url)}</span>
        <a className="ticket-host" href={m.url} target="_blank" rel="noopener noreferrer">{hostOf(m.url)} ↗</a>
        {cap('What is it?', 'ticket-cap')}
      </div>
    );
  }
  if (m.kind === 'photo') {
    return <div className="pinup polaroid" style={style}>{x}<img className="polaroid-pic" src={m.photo} alt={m.caption || 'Something you made'} />{cap('What is it?', 'polaroid-cap')}</div>;
  }
  return <div className="pinup index" style={style}>{x}{cap('What is it?', 'index-cap')}</div>;
}

/**
 * A link, and a screenshot of it if they have one. The screenshot is theirs,
 * from their phone: the app never visits the link to make a preview, because a
 * server that fetches any address it is handed can be pointed at places it
 * should never reach.
 */
function AddLink({ onAdd, onCancel, onDraft }: { onAdd: (url: string, caption: string, photo?: string) => void; onCancel: () => void; onDraft: (d: Draft | null) => void }) {
  const [raw, setRaw] = useState('');
  const [caption, setCaption] = useState('');
  const [shot, setShot] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const shotRef = useRef<HTMLInputElement>(null);
  const url = asUrl(raw);
  const canPaste = typeof navigator !== 'undefined' && !!navigator.clipboard?.readText;
  const paste = async () => { try { const t = await navigator.clipboard.readText(); if (t) setRaw(t.trim()); } catch { /* the phone said no; typing still works */ } };
  const onShot = (f?: File) => { if (!f) return; setBusy(true); void photoForCard(f, MADE_EDGE).then(setShot).finally(() => setBusy(false)); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onDraft(url ? { flush: () => onAdd(url, caption.trim(), shot), busy } : null); }, [url, caption, shot, busy]);
  useEffect(() => () => onDraft(null), [onDraft]);
  return (
    <form className="add-sheet" onSubmit={(e) => { e.preventDefault(); if (url && !busy) onAdd(url, caption.trim(), shot); }}>
      <span className="label">A link to something you made</span>
      <div className="row" style={{ gap: 8 }}>
        <input className="input" value={raw} onChange={(e) => setRaw(e.target.value)} placeholder="claude.ai/public/artifacts/…" inputMode="url" autoCapitalize="off" autoCorrect="off" spellCheck={false} autoFocus style={{ flex: 1, minWidth: 0 }} />
        {canPaste && <button type="button" className="btn small butter" onClick={paste}>Paste</button>}
      </div>
      <input className="input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="What is it? A tide clock for my street" maxLength={60} />
      <input ref={shotRef} type="file" accept="image/*" hidden onChange={(e) => onShot(e.target.files?.[0])} />
      <button type="button" className={`shot-btn ${shot ? 'has' : ''}`} onClick={() => shotRef.current?.click()}>
        {shot ? <img src={shot} alt="Your screenshot" /> : <span className="add-ico">🖼️</span>}
        <span>{busy ? 'Adding it…' : shot ? 'Screenshot added · change it' : 'Add a screenshot of it, so we can see it'}</span>
      </button>
      <span className="hint">A site, an app, a tool you built with Claude, a song, a blog. {raw && !url ? 'That doesn’t look like a link yet.' : ''}</span>
      <div className="row between">
        <button type="button" className="link" onClick={onCancel}>Never mind</button>
        <button type="submit" className="btn small" disabled={!url || busy}>Pin it up</button>
      </div>
    </form>
  );
}

function AddNote({ onAdd, onCancel, onDraft }: { onAdd: (caption: string) => void; onCancel: () => void; onDraft: (d: Draft | null) => void }) {
  const [t, setT] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onDraft(t.trim() ? { flush: () => onAdd(t.trim()), busy: false } : null); }, [t]);
  useEffect(() => () => onDraft(null), [onDraft]);
  return (
    <form className="add-sheet" onSubmit={(e) => { e.preventDefault(); if (t.trim()) onAdd(t.trim()); }}>
      <span className="label">Something you made, in a few words</span>
      <input className="input hand" value={t} onChange={(e) => setT(e.target.value)} placeholder="A birthday cake shaped like a boat" maxLength={60} autoFocus />
      <div className="row between">
        <button type="button" className="link" onClick={onCancel}>Never mind</button>
        <button type="submit" className="btn small" disabled={!t.trim()}>Pin it up</button>
      </div>
    </form>
  );
}

// ── 3. the next thing, and the dice ───────────────────────────────────────────

function NextCard({ value, onChange, made, goals }: { value: string; onChange: (v: string) => void; made: MadeThing[]; goals: string[] }) {
  const ref = useGrow(value);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [from, setFrom] = useState<'crew' | 'deck' | null>(null);
  const [rolling, setRolling] = useState(false);
  const [rolls, setRolls] = useState(0);
  const shown = useRef<string[]>([]);
  const [crewOn] = useState(() => loadProfile().crew === 'on');

  const roll = async () => {
    if (rolling) return;
    setRolling(true);
    const started = Date.now();
    // a box holding one of the dice's own ideas is not a draft of theirs to build on
    const draft = shown.current.includes(value.trim()) ? '' : value;
    const r = await rollIdeas(made, shown.current, { goals, draft });
    // the die gets a proper tumble even when the answer is instant
    await new Promise((res) => setTimeout(res, Math.max(0, 700 - (Date.now() - started))));
    shown.current = [...shown.current, ...r.ideas.map((x) => x.idea)];
    setIdeas(r.ideas); setFrom(r.from); setRolling(false); setRolls((n) => n + 1);
    track('whoami_rolled', { from: r.from, roll: rolls + 1, things: made.length });
  };

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="indexcard">
        <textarea ref={ref} rows={2} value={value} onChange={(e) => onChange(e.target.value)} placeholder="However big or daft" aria-label="Next I want to make" maxLength={140} />
      </div>
      <button type="button" className={`dice-btn ${rolling ? 'rolling' : ''}`} onClick={roll} disabled={rolling}>
        <span className="die" aria-hidden="true"><i /><i /><i /><i /><i /></span>
        <span>{rolling ? (crewOn && made.length ? 'Looking at your work…' : 'Rolling…') : rolls ? 'Roll again' : 'Stuck? Roll for ideas'}</span>
      </button>
      {/* the pictures go to the crew for the roll, so say so before the tap, not after */}
      {!rolls && !rolling && crewOn && made.length > 0 && <span className="hint" style={{ textAlign: 'center', marginTop: -8 }}>The Moon looks at the work you pinned up, then deals three.</span>}
      {ideas.length > 0 && !rolling && (
        <div className="ideas" key={rolls}>
          {from === 'crew' && <span className="ideas-from"><Avatar who="the-moon" size={22} />The Moon, from your work</span>}
          {ideas.map(({ idea, because }, i) => (
            <button type="button" key={idea} className={`idea ${value.trim() === idea ? 'on' : ''}`} style={{ animationDelay: `${i * 110}ms`, '--tilt': `${[-1.4, 1, -0.6][i % 3]}deg` } as React.CSSProperties}
              onClick={() => { onChange(idea); track('whoami_took_idea', { from, i }); }}>
              {idea}
              {because && <small className="idea-why">{because}</small>}
            </button>
          ))}
          <span className="hint" style={{ textAlign: 'center' }}>Take one, change it, or ignore them all.</span>
        </div>
      )}
    </div>
  );
}
